import { Injectable } from '@angular/core';
import { EnvService } from './env.service';
import { BehaviorSubject, Observable } from 'rxjs';
import * as LDClient from 'launchdarkly-js-client-sdk';



// PCIS-10069 Feature Flag Service using LaunchDarkly
@Injectable({ providedIn: 'root' })
export class FeatureFlagService {
  constructor(private envService: EnvService) {}
  private ldClient: LDClient.LDClient | null = null;
  private flagsSubject = new BehaviorSubject<{ [key: string]: any }>({});
  public flags$: Observable<{ [key: string]: any }> = this.flagsSubject.asObservable();

  async initialize(context: LDClient.LDContext) {
    try {
      const sdkKey = this.envService.launchDarklyClientId;

      // Validate SDK key exists
      if (!sdkKey) {
        console.warn('[FeatureFlagService] LaunchDarkly SDK key not configured for this environment');
        return;
      }

      // Validate context has required keys
      if (!this.isValidContext(context)) {
        console.warn('[FeatureFlagService] Invalid context provided, skipping initialization');
        return;
      }

      this.ldClient = LDClient.initialize(sdkKey, context);
      await this.ldClient.waitForInitialization();
      this.updateAllFlags();
      this.ldClient.on('change', () => this.updateAllFlags());
    } catch (error) {
      console.error('[FeatureFlagService] Initialization error:', error);
    }
  }

  /**
   * Validates that the LaunchDarkly context has proper structure and non-empty keys
   */
  private isValidContext(context: LDClient.LDContext): boolean {
    if (!context) return false;

    const contextObj = context as any;

    // For multi-context, validate each context kind
    if (contextObj.kind === 'multi') {
      // Validate user context
      if (!contextObj.user?.key || contextObj.user.key === 'null' || contextObj.user.key === '') {
        return false;
      }

      // Validate organization context
      if (!contextObj.organization?.key || contextObj.organization.key === 'null' || contextObj.organization.key === '') {
        return false;
      }

      return true;
    }

    // For single context, just validate the key
    return !!contextObj.key && contextObj.key !== 'null' && contextObj.key !== '';
  }

  private updateAllFlags(): void {
    try {
      if (!this.ldClient) return;
      const allFlags = this.ldClient.allFlags();
      this.flagsSubject.next(allFlags);
    } catch (error) {
      console.error('[FeatureFlagService] Error updating flags:', error);
    }
  }

  /* Returns true if the feature flag is enabled, false otherwise */
  isFeatureEnabled(flagKey: string): boolean {
    try {
      const flags = this.flagsSubject.getValue();
      if (!flags) return true;
      
      return !!flags[flagKey];
    } catch (error) {
      console.error('[FeatureFlagService] Error checking feature flag:', error);
      return true;
    }
  }

  /**
   * Returns the value of a feature flag key (could be boolean, string, number, etc.)
   * Returns undefined if LaunchDarkly client is not initialized.
   */
  getFlagValue<T = any>(flagKey: string): T | undefined {
    const flags = this.flagsSubject.getValue();
    return flags[flagKey];
  }
  
}